import React, { useState, useEffect} from 'react';
import axios from 'axios';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { notify } from '../../context/FeedbackContext';
import './RestaurantsPage.css';
import { FaSearch, FaFilter, FaEdit } from 'react-icons/fa';
import { FiPlusCircle } from 'react-icons/fi';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';
import HasPermi from '../../components/HasPermi'; // 新增：导入 HasPermi 组件

function RefId({ value }) {
  const [copied, setCopied] = React.useState(false);
  if (!value) return <div className="tenant_id">ID: -</div>;
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="tenant_id ref-id-wrapper" onClick={handleCopy}>
      <span className="ref-id-short">ID: {value.slice(0, 8)}...</span>
      <span className="ref-id-tooltip">{copied ? 'Copied!' : value}</span>
    </div>
  );
}

const EMPTY_FORM = { code: '', tenantName: '', tenantAddress: '', ownUser: '', postalCode: '', uen: '', contact: '', status: 'Active' };

const PAGE_SIZE = 6;

function RestaurantsPage({ onNavigate }) {

  const handleNavClick = (item) => {
    if (onNavigate) {
      onNavigate(getRouteFromItem(item));
    }
  }

  const [restaurantSection, setRestaurantSection] = useState('Restaurants');
  const [view, setView] = useState('list');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All');
  const [restaurants, setRestaurants] = useState([]);
  const [owners, setOwners] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const statusLabel = (status) => status === 1 ? 'Active' : 'Inactive';

  const fetchRestaurants = async (page, keyword, status) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ currentPage: page });
      if (keyword) params.append('keyword', keyword);
      if (status !== 'All') params.append('status', status === 'Active' ? 1 : 0);
      const response = await axios.get(buildApiUrl(`/restaurants/page?${params.toString()}`), { headers: getAuthHeaders() });
      const d = response.data.data || {};
      setRestaurants(d.list || []);
      setTotalCount(d.totalCount || 0);
      setPageSize(d.pageSize || PAGE_SIZE);
    } catch (err) {
      console.error("Failed to fetch restaurants", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await axios.get(buildApiUrl('/users/list/type?type=Owner'), { headers: getAuthHeaders() });
      setOwners(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch owner", err);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRestaurants(currentPage, search, statusFilter);
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [currentPage, search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchOwners();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const pageRestaurants = restaurants;
  const totalResults = totalCount;
  const showingFrom = totalResults === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo = Math.min(currentPage * pageSize, totalResults);


  const openAdd = () => {
    setForm({
      code: '',
      tenantName: '',
      tenantAddress: '',
      postalCode: '',
      contact: '',
      uen: '',
      status: 'Active',
      userReference: ''
    });
    setFormErrors({});
    setView('add');
  };

  const openEdit = (restaurant) => {
    const owner = owners.find(o => o.reference === restaurant.userReference);
    setForm({
      reference: restaurant.reference,
      code: restaurant.code,
      tenantName: restaurant.tenantName,
      tenantAddress: restaurant.tenantAddress,
      postalCode: restaurant.postalCode,
      uen: restaurant.uen,
      contact: restaurant.contact,
      status: statusLabel(restaurant.status),
      userReference: restaurant.userReference || (owner ? owner.reference : '')
    });
    setFormErrors({});
    setView('edit');
  };

  const backToList = () => { setView('list'); setForm(EMPTY_FORM); setFormErrors({}); };

  const handleFormChange = (e) =>
    {
      const {name, value} = e.target;

      // Auto-fill functionality for owner dropdown list
      if (name === 'userReference')
      {
        if (!value)
        {
          setForm(prev => ({ ...prev, userReference: '', contact: ''}));
          return;
        }

        const selectedOwner = owners.find(owner => owner.reference === value);
        setForm(prev => ({
          ...prev,
          userReference: value,
          contact: selectedOwner ? selectedOwner.contact : ''
        }));
      }

      else {
        setForm(prev => ({ ...prev, [name]: value}));
      }
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    };

  const validateRestaurant = (isCreate) => {
    const errors = {};
    if (!form.tenantName) errors.tenantName = 'Restaurant name is required.';
    if (!form.code) errors.code = 'Restaurant code is required.';
    if (isCreate && !form.userReference) errors.userReference = 'Owner is required.';
    if (!form.tenantAddress) errors.tenantAddress = 'Address is required.';
    else if (form.tenantAddress.length > 200) errors.tenantAddress = 'Address cannot exceed 200 characters.';
    if (!form.postalCode) errors.postalCode = 'Postal code is required.';
    if (!form.uen) errors.uen = 'UEN is required.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Creating a restaurant detail
  const handleCreate = async () => {
    if (!validateRestaurant(true)) return;
    const statusValue = form.status === 'Active' ? 1 : 0;

    // ADD THIS LINE:
    console.log("PAYLOAD BEING SENT TO SPRING BOOT:", form);

    try {
      const response = await fetch(buildApiUrl('/restaurants/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          userReference: form.userReference,
          tenantName: form.tenantName,
          code: form.code,
          tenantAddress: form.tenantAddress,
          postalCode: form.postalCode,
          uen: form.uen,
          contact: form.contact,
          status: statusValue }),
      });
      const data = await response.json();
      if (data.code === 200) {
        notify('Restaurant created successfully!', 'success');
        backToList();
        fetchRestaurants(currentPage, search, statusFilter);
      } else {
        notify(data.msg || 'Failed to create restaurant.', 'error');
      }
    } catch (err) {
      console.error('Failed to create restaurant.', err);
    }
  };

  // Updating a Restaurant
  const handleUpdate = async () => {
    if (!validateRestaurant(false)) return;
    const statusValue = form.status === 'Active' ? 1 : 0;
    try {
      const response = await fetch(buildApiUrl('/restaurants/update'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          reference: form.reference,
          userReference: form.userReference,
          tenantName: form.tenantName,
          code: form.code,
          tenantAddress: form.tenantAddress,
          postalCode: form.postalCode,
          uen: form.uen,
          contact: form.contact,
          status: statusValue }),
      });
      const data = await response.json();
      if (data.code === 200) {
        notify(data.message || 'Restaurant updated successfully!', 'success');

        // Closing the Edit Modal
        backToList();

        await fetchRestaurants(currentPage, search, statusFilter);


      } else {
        notify(data.message || 'Failed to update restaurant.', 'error');
      }
    } catch (err) {
      console.error('Failed to update restaurant', err);
    }
  };

  const [goToPage, setGoToPage] = React.useState('');

  const renderPageButtons = () => {
    const buttons = [];
    buttons.push(
      <button key="prev" className={`pg-btn${currentPage === 1 ? ' disabled' : ''}`}
        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>
        Previous
      </button>
    );
    const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
    const end = Math.min(totalPages, start + 2);
    buttons.push(<span key="dots-start" className="pg-dots" style={{ visibility: start > 1 ? 'visible' : 'hidden' }}>...</span>);
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} className={`pg-btn${currentPage === i ? ' active' : ''}`}
          onClick={() => setCurrentPage(i)}>
          {i}
        </button>
      );
    }
    buttons.push(<span key="dots-end" className="pg-dots" style={{ visibility: end < totalPages ? 'visible' : 'hidden' }}>...</span>);
    buttons.push(
      <button key="next" className={`pg-btn${currentPage === totalPages ? ' disabled' : ''}`}
        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>
        Next
      </button>
    );
    return buttons;
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="restaurant-root-wrapper">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item =>
            (
              <li
                key={item}
                className={item === 'Restaurant Management' ? 'active' : ''}
                onClick={() => handleNavClick(item)}
              >
                <span className="nav-icon">{navIcons[item]}</span> {item}
              </li>
            )
          )}
        </ul>
      </nav>
      {/* Main Content */}
      <main className="restaurant-main">
        <div className="admin-breadcrumb">
          <span className="breadcrumb-link" onClick={() => handleNavClick('Home')}>Home</span>
          <span className="breadcrumb-sep">›</span>
          <span className="breadcrumb-current">Restaurant Management</span>
        </div>
        <h1 className="admin-page-title">Restaurant Management</h1>
        <p className="admin-page-subtitle">Manage all restaurants, view their details and account status.</p>

        {/* 整个餐厅管理区域 - 需要 restaurants:page 权限 */}
        <HasPermi permission="restaurants:page">
          <div className="user-list-card">
            <div className="user-list-header">
              <h2 className="user-list-title">Restaurant List</h2>
              <div className="user-list-actions">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input type="text" placeholder="Search restaurants..." value={search} onChange={handleSearch} />
                </div>
                <FaFilter className="filter-icon" />
                <select
                  className="filter-select"
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="All">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {/* 添加餐厅按钮 - 需要 restaurants:create 权限 */}
                <HasPermi permission="restaurants:create">
                  <button className="add-restaurant-btn" onClick={openAdd}><FiPlusCircle /> Add Restaurant</button>
                </HasPermi>
              </div>
            </div>

            <table className="restaurant-table">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>CODE</th>
                  <th>OWN USER</th>
                  <th>ADDRESS</th>
                  <th>POSTAL CODE</th>
                  <th>UEN</th>
                  <th>PHONE</th>
                  <th>STATUS</th>
                  <th>CREATED AT</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageRestaurants.map(restaurant => (
                  <tr key={restaurant.id}>
                    <td>
                      <div className="tenant_name">{restaurant.tenantName}</div>
                      <RefId value={restaurant.reference} />
                    </td>
                    <td>{restaurant.code}</td>
                    <td>{restaurant.ownUser}</td>
                    <td>{restaurant.tenantAddress || '-'}</td>
                    <td>{restaurant.postalCode || '-'}</td>
                    <td>{restaurant.uen || '-'}</td>
                    <td>{restaurant.contact}</td>
                    <td>
                      <span className={`status-badge status-${statusLabel(restaurant.status)
                        .toLowerCase()}`}>{statusLabel(restaurant.status)}</span>
                    </td>
                    <td>{restaurant.createdAt}</td>
                    <td>
                      {/* 编辑按钮 - 需要 restaurants:update 权限 */}
                      <HasPermi permission="restaurants:update">
                        <button className="edit-btn" onClick={() => openEdit(restaurant)}><FaEdit /></button>
                      </HasPermi>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination-row">
              <span className="pagination-info">Showing {showingFrom} to {showingTo} of {totalResults} results</span>
              <div className="pagination-buttons">{renderPageButtons()}</div>
              <div className="pagination-goto">
                <span>Go to page</span>
                <input
                  className="pg-goto-input"
                  type="number"
                  min="1"
                  max={totalPages}
                  value={goToPage}
                  onChange={e => setGoToPage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const p = parseInt(goToPage);
                      if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); }
                    }
                  }}
                  placeholder=""
                />
                <button className="pg-btn" onClick={() => {
                  const p = parseInt(goToPage);
                  if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); }
                }}>Go</button>
              </div>
            </div>
          </div>
        </HasPermi>

        {/* 如果没有 restaurants:page 权限，显示提示信息 */}
        <HasPermi
          permission="restaurants:page"
          fallback={
            <div className="no-permission-message">
              <p>You don't have permission to view restaurants. Please contact administrator.</p>
            </div>
          }
        >
          {/* 这个空的 HasPermi 用于显示回退内容 */}
        </HasPermi>
      </main>

      {/* Modal - 需要相应的权限 */}
      <HasPermi permission={['restaurants:create', 'restaurants:update']} any>
        {(view === 'add' || view === 'edit') && (
          <div className="rp-modal-overlay">
            <div className="rp-modal" onClick={e => e.stopPropagation()}>
              <button className="rp-modal-close" onClick={backToList}>×</button>
              <h2 className="rp-modal-title">
                {view === 'add' ? 'Create New Restaurant' : 'Edit Restaurant'}
              </h2>
              <p className="rp-modal-subtitle">
                {view === 'add' ? 'Add a new restaurant to the system. Fill in all required fields.' : 'Update the restaurant details. Modify the fields as needed.'}
              </p>

              <div className="form-field">
                <label>Restaurant Name <span className="required">*</span></label>
                <input className={formErrors.tenantName ? 'input-error' : ''} name="tenantName" value={form.tenantName} onChange={handleFormChange} placeholder="Enter restaurant name" />
                {formErrors.tenantName && <p className="field-error">{formErrors.tenantName}</p>}
              </div>

              <div className="form-field">
                <label>Restaurant Code <span className="required">*</span></label>
                <input className={formErrors.code ? 'input-error' : ''} name="code" value={form.code} onChange={handleFormChange} placeholder="Enter restaurant code" readOnly={view === 'edit'} style={view === 'edit' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#555' } : {}} />
                {formErrors.code && <p className="field-error">{formErrors.code}</p>}
              </div>

              <div className="form-field">
                <label>Owner <span className="required">*</span></label>
                <select
                  className={formErrors.userReference ? 'input-error' : ''}
                  name="userReference"
                  value={form.userReference || ''}
                  onChange={handleFormChange}
                >
                  <option value="">Select owner</option>
                  {owners.map((owner) => (
                    <option key={owner.reference} value={owner.reference}>
                      {owner.first_name} {owner.last_name}
                    </option>
                  ))}
                </select>
                {formErrors.userReference && <p className="field-error">{formErrors.userReference}</p>}
              </div>

              <div className="form-field">
                <label>Address <span className="required">*</span></label>
                <input className={formErrors.tenantAddress ? 'input-error' : ''} name="tenantAddress" value={form.tenantAddress} onChange={handleFormChange} placeholder="Enter address" />
                {formErrors.tenantAddress && <p className="field-error">{formErrors.tenantAddress}</p>}
              </div>

              <div className="form-field">
                <label>Postal Code <span className="required">*</span></label>
                <input className={formErrors.postalCode ? 'input-error' : ''} name="postalCode" value={form.postalCode} onChange={handleFormChange} placeholder="Enter postal code" />
                {formErrors.postalCode && <p className="field-error">{formErrors.postalCode}</p>}
              </div>

              <div className="form-field">
                <label>UEN <span className="required">*</span></label>
                <input className={formErrors.uen ? 'input-error' : ''} name="uen" value={form.uen} onChange={handleFormChange} placeholder="Enter UEN" />
                {formErrors.uen && <p className="field-error">{formErrors.uen}</p>}
              </div>

              <div className="form-field">
                <label>Contact Phone</label>
                <input
                  name="contact"
                  value={form.contact}
                  readOnly
                  placeholder="Auto-filled when owner is selected"
                  className={form.contact ? 'autofilled-input' : ''}
                />
              </div>

              <div className="form-field">
                <label>Status <span className="required">*</span></label>
                <select name="status" value={form.status} onChange={handleFormChange}>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>

              <div className="form-actions">
                <button className="cancel-btn" onClick={backToList}>Cancel</button>
                <button className="submit-btn" onClick={view === 'add' ? handleCreate : handleUpdate}>
                  {view === 'add' ? 'Create Restaurant' : 'Update Restaurant'}
                </button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>
    </div>
  );
}

export default RestaurantsPage;
